import { useState, useEffect } from 'react';
import type { CustomSelectProps, OptionItem } from './types';

// Extracted hooks and closures from customSelectCheckbox per spec §4.3
/**
 * Backs the multi-select-with-checkboxes control, holding the selected values and the select-all flag. Syncs selection from the value prop, keeps selectAll true while every option is selected, buffers changes locally and only reports them to props.onChange once the dropdown closes.
 *
 * @param props - Options, field name, current value and the change callback.
 * @returns The selection state and the change, visibility and select-all handlers.
 */
export function useCustomSelectCheckbox(props: CustomSelectProps) {
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const { options, name = '' } = props;

  useEffect(() => {
    const initialValues = props.value;
    setSelectedValues(initialValues);
  }, [props.value]);

  useEffect(() => {
    setSelectAll(selectedValues.length === options.length);
  }, [selectedValues, options]);

  /**
   * Records the newly selected option values for the checkbox select. Stores the provided array in local selectedValues state without notifying the parent.
   *
   * @param values - The new list of selected values.
   * @returns Nothing.
   */
  const handleChange = (values: string[]) => {
    setSelectedValues(values);
  };

  /**
   * Commits the buffered checkbox selection when the dropdown closes. Calls props.onChange with the field name and selectedValues only once the dropdown is no longer focused.
   *
   * @param focusOn - Whether the dropdown is now focused or open.
   * @returns Nothing.
   */
  const handleOnDropdownVisibleChange = (focusOn: boolean) => {
    if (!focusOn) {
      props.onChange?.(name, selectedValues);
    }
  };

  /**
   * Applies the select-all checkbox toggle. Selects every option's value when checked and otherwise keeps only the first option's value, updating both the selectAll flag and the selected values.
   *
   * @param e - Change event from the select-all checkbox.
   * @returns Nothing.
   */
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    /**
     * Computes the list of selected values resulting from the All toggle. Yields every option's value when the box is checked, otherwise an array holding only the first option's value.
     *
     * @returns The resulting selected values.
     */
    const values = checked ? options.map((opt: OptionItem) => opt.value) : [options[0].value];
    setSelectAll(checked);
    setSelectedValues(values);
  };

  return {
    selectedValues,
    selectAll,
    handleChange,
    handleOnDropdownVisibleChange,
    handleSelectAll,
  };
}
